import React, { useMemo, useState } from 'react';
import AvailableWalletBalance from '../shared/crypto/AvailableWalletBalance';
import './css/DesktopTransferPanel.css';

/** Desktop-only wallet transfer UI shared by Home and Browser.
 *  All balances, validation, transfer submission, and account state stay with the caller.
 */
export default function DesktopTransferPanel({
  language = 'english', theme = 'dark', amount = '', recipient = '',
  usernames = [], balanceWei, commissionRate = 0.05,
  onAmountChange, onRecipientChange, onContinue, mobile = false,
}) {
  const [focused, setFocused] = useState(false);
  const [picked, setPicked] = useState(false);
  const es = language === 'spanish';
  const dark = theme !== 'light';
  const amountNumber = Number(amount || 0);
  const amountValid = Number.isFinite(amountNumber) && amountNumber > 0 && (mobile || amountNumber <= 999);
  const fee = Number.isFinite(Number(commissionRate)) ? Math.max(0, Math.min(1, Number(commissionRate))) : 0.05;
  const recipientText = String(recipient || '').trim();
  const safeAmount = amountValid ? amountNumber : 0;
  const format = (n) => Number(n).toLocaleString(es ? 'es-ES' : 'en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const balance = Number(balanceWei) / 1e18;
  const hasBalance = balanceWei != null && Number.isFinite(balance) && balance >= 0;

  const matches = useMemo(() => {
    const q = recipientText.toLowerCase();
    if (q.length < 2) return [];
    return (Array.isArray(usernames) ? usernames : [])
      .map((user) => typeof user === 'string' ? user : user?.name || user?.username || '')
      .map((name) => String(name).trim())
      .filter((name) => name && name.toLowerCase().includes(q))
      .sort((a, b) => (Number(!a.toLowerCase().startsWith(q)) - Number(!b.toLowerCase().startsWith(q))) || a.localeCompare(b))
      .slice(0, 8);
  }, [recipientText, usernames]);

  const insufficient = hasBalance && amountValid && amountNumber >= balance;
  const canContinue = amountValid && !!recipientText && !insufficient;
  const showMatches = focused && !picked && matches.length > 0;

  return (
    <div role="region" className={'tbp-desktop-transfer ' + (mobile ? 'tbp-desktop-transfer--mobile ' : '') + (dark ? 'tbp-desktop-transfer--dark' : 'tbp-desktop-transfer--light')}
      aria-label={es ? 'Transferir a una cuenta Blender' : 'Transfer to a Blender account'}>
      <header className="tbp-desktop-transfer__heading">
        <h2>{es ? 'Transferir Fondos' : 'Transfer Funds'}</h2>
        <p>{es ? 'Envía fondos a otra cuenta Blender.' : 'Send funds to another Blender account.'}</p>
      </header>

      <AvailableWalletBalance balanceWei={balanceWei} language={language} theme={theme} context="transfer" />

      <div className="tbp-desktop-transfer__group">
        <label htmlFor="tbp-desktop-transfer-amount">{es ? 'Cantidad a transferir' : 'Amount to transfer'}</label>
        <div className="tbp-desktop-transfer__amount-field">
          <input
            id="tbp-desktop-transfer-amount" type="number" min="0" max={mobile ? undefined : 999} step="any"
            inputMode="decimal" autoComplete="off" placeholder="0.00"
            value={amount == null ? '' : amount}
            onChange={(event) => {
              const next = event.target.value;
              if (next === '' || (Number.isFinite(Number(next)) && (mobile || Number(next) <= 999))) onAmountChange(next);
            }}
          />
          <span className="tbp-desktop-transfer__unit">USD</span>
        </div>
        {insufficient && <p className="tbp-desktop-transfer__validation" role="alert">{es ? 'La cantidad debe ser menor que tu saldo disponible.' : 'Amount must be less than your available balance.'}</p>}
      </div>

      <div className="tbp-desktop-transfer__group tbp-desktop-transfer__recipient-group">
        <label htmlFor="tbp-desktop-transfer-recipient">{es ? 'Destinatario' : 'Recipient'}</label>
        <div className="tbp-desktop-transfer__recipient-field">
          <span className="tbp-desktop-transfer__at" aria-hidden="true">@</span>
          <input
            id="tbp-desktop-transfer-recipient" type="text" autoComplete="off" spellCheck={false}
            maxLength={20} placeholder={es ? 'Nombre de usuario' : 'Blender username'}
            value={recipient == null ? '' : recipient}
            onChange={(event) => { setPicked(false); onRecipientChange(event.target.value); }}
            onFocus={() => { setFocused(true); setPicked(false); }}
            onBlur={() => setFocused(false)}
            aria-autocomplete="list" aria-expanded={showMatches}
            aria-controls="tbp-desktop-transfer-suggestions"
          />
        </div>
        {showMatches && <div className="tbp-desktop-transfer__suggestions" id="tbp-desktop-transfer-suggestions" role="listbox">
          {matches.map((name) => <button key={name} type="button" role="option" aria-selected={false}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => { onRecipientChange(name.toUpperCase()); setPicked(true); setFocused(false); }}>
            <span className="tbp-desktop-transfer__avatar" aria-hidden="true">{name.charAt(0).toUpperCase()}</span>
            <span>{name.toUpperCase()}</span>
            <span aria-hidden="true" className="tbp-desktop-transfer__choice-arrow">↗</span>
          </button>)}
        </div>}
        <p className="tbp-desktop-transfer__hint">{es ? 'Busca por nombre de usuario de Blender.' : 'Search by Blender username.'}</p>
      </div>

      <div className="tbp-desktop-transfer__summary">
        <div><span>{es ? 'Comisión de la plataforma' : 'Platform fee'} ({format(fee * 100)}%)</span><strong>{format(safeAmount * fee)} USD</strong></div>
        <div className="tbp-desktop-transfer__total"><span>{es ? 'El destinatario recibe' : 'Recipient receives'}</span><strong>{format(safeAmount * (1 - fee))} USD</strong></div>
      </div>

      <button type="button" className="tbp-desktop-transfer__continue" disabled={!canContinue}
        onClick={() => onContinue(recipientText, safeAmount)}>
        {es ? 'Continuar a confirmación' : 'Continue to confirmation'}
        <span aria-hidden="true">→</span>
      </button>
      <p className="tbp-desktop-transfer__footnote">{es ? 'Revisa los detalles antes de autorizar la transferencia.' : 'Review the details before authorizing your transfer.'}</p>
    </div>
  );
}
